import { Box, styled } from '@planne-software/uni/mui/material';
import { Button } from '@planne-software/uni/Button';
import { CountDown, countDownClasses } from '@planne-software/uni';
import { uni } from '@planne-software/uni/v2';

import { tabletBreakpoint } from '@src/common/utils/mediaQuery';
import { uniTypography } from '@src/fonts/typography/uniTypography';

export const MethodBodyRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-400'),
  width: '100%',
});

export const StepList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-400'),
  width: '100%',
  [tabletBreakpoint]: {
    gap: uni('scale-200'),
  },
}));

export const StepRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${uni('scale-200')};
  width: 100%;
  ${uniTypography('text-md', 'medium')}
  color: ${uni('fg-neutral-static-default-body')};

  & svg {
    flex-shrink: 0;
  }

  ${tabletBreakpoint} {
    align-items: flex-start;
    ${uniTypography('text-sm', 'medium')}
  }
`;

export const ActionRow = styled(Box)`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;

  ${tabletBreakpoint} {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: ${uni('scale-400')};
  }
`;

export const SecurePurchase = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${uni('scale-200')};
  ${uniTypography('text-md', 'medium')}
  color: ${uni('fg-success-default-rest')};

  & svg {
    flex-shrink: 0;
  }
`;

export const GenerateButton = styled(Button)`
  flex-shrink: 0;

  ${tabletBreakpoint} {
    width: 100%;
  }
`;

export const PixCardRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: uni('spacing-24-16'),
  width: '100%',
  padding: uni('padding-content'),
  borderRadius: uni('border-radius-md'),
  border: `1px solid ${uni('border-static-content-default')}`,
  backgroundColor: uni('bg-static-content-default'),
  [tabletBreakpoint]: {
    borderRadius: uni('border-radius-sm'),
  },
}));

export const PixCardHeader = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: uni('scale-300'),
  width: '100%',
});

export const PixCardTitleGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-100'),
  width: '100%',
});

export const CodeGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-200'),
  width: '100%',
});

export const ChangeMethodButton = styled(Button)`
  ${uniTypography('text-sm', 'semibold')}
  border-radius: ${uni('border-radius-xs')};
  padding: ${uni('scale-250')} ${uni('scale-300')};
  text-transform: none;
  align-self: flex-start;

  ${tabletBreakpoint} {
    width: 100%;
    align-self: stretch;
  }
`;

export const PixCardTitle = styled('h2')`
  ${uniTypography('text-lg', 'semibold')}
  color: ${uni('fg-neutral-static-default-heading')};
  margin: 0;
`;

export const PixCardDescription = styled('p')`
  ${uniTypography('text-md', 'medium')}
  color: ${uni('fg-neutral-static-default-body')};
  margin: 0;

  ${tabletBreakpoint} {
    ${uniTypography('text-sm', 'medium')}
  }
`;

export const CodeLayout = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: uni('spacing-24-16'),
  width: '100%',
  [tabletBreakpoint]: {
    flexDirection: 'column',
  },
}));

export const QrCodeFrame = styled(Box)(() => ({
  width: '266px',
  height: '266px',
  flexShrink: 0,
  '& img': { display: 'block', width: '100%', height: '100%' },
  [tabletBreakpoint]: {
    order: 2,
    width: '100%',
    height: 'auto',
    aspectRatio: '1 / 1',
    padding: uni('scale-400'),
  },
}));

export const CodeColumn = styled(Box)(() => ({
  display: 'flex',
  flex: '1 0 0',
  minWidth: 0,
  flexDirection: 'column',
  gap: uni('spacing-24-16'),
  [tabletBreakpoint]: {
    order: 1,
    width: '100%',
  },
}));

export const PixTimer = styled(CountDown)`
  &&& .${countDownClasses.timer} {
    ${uniTypography('display-md', 'bold')}
  }
`;

export const PixCodeText = styled('p')`
  ${uniTypography('text-lg', 'medium')}
  color: ${uni('fg-neutral-static-default-body')};
  margin: 0;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${tabletBreakpoint} {
    ${uniTypography('text-md', 'medium')}
  }
`;

export const CopyButton = styled(Button)`
  ${uniTypography('text-sm', 'semibold')}
  width: 100%;
  border-radius: ${uni('border-radius-xs')};
  padding: ${uni('scale-250')} ${uni('scale-300')};
  text-transform: none;
`;

export const HowToRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-400'),
  width: '100%',
  padding: uni('padding-card-components'),
  '&[data-flush="true"]': {
    padding: 0,
    [tabletBreakpoint]: {
      padding: uni('padding-card-components'),
    },
  },
}));

export const HowToTitle = styled('p')`
  ${uniTypography('text-md', 'semibold')}
  color: ${uni('fg-neutral-static-default-body')};
  margin: 0;
`;

export const HowToRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${uni('scale-200')};
  width: 100%;
  ${uniTypography('text-sm', 'medium')}
  color: ${uni('fg-neutral-static-default-caption')};

  & svg {
    flex-shrink: 0;
  }

  ${tabletBreakpoint} {
    align-items: flex-start;
  }
`;

export const ExpiredAlertRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-300'),
  width: '100%',
  padding: uni('scale-400'),
  borderRadius: uni('border-radius-sm'),
  border: `4px solid ${uni('border-alert-default-rest')}`,
  backgroundColor: uni('bg-alert-subtle-rest'),
  [tabletBreakpoint]: {
    gap: uni('scale-200'),
    padding: uni('scale-300'),
  },
}));

export const ExpiredAlertText = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-200'),
  width: '100%',
});

export const ExpiredAlertHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${uni('scale-200')};
  ${uniTypography('text-sm', 'semibold')}
  color: ${uni('fg-neutral-action-on-color-subtle-rest')};

  & svg {
    flex-shrink: 0;
  }

  ${tabletBreakpoint} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ExpiredAlertDescription = styled('p')`
  ${uniTypography('text-sm', 'regular')}
  color: ${uni('fg-neutral-action-on-color-subtle-rest')};
  margin: 0;
`;

export const RetryButton = styled(Button)`
  && {
    ${uniTypography('text-xs', 'semibold')}
    padding: ${uni('scale-200')} ${uni('scale-300')};
    border-radius: ${uni('border-radius-xs')};
    text-transform: none;
    align-self: flex-start;
  }
`;

export const ReceiverDisclaimerCard = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: uni('scale-300'),
  width: '100%',
  padding: uni('padding-content'),
  borderRadius: uni('border-radius-md'),
  border: `1px solid ${uni('border-static-content-highlight')}`,
  backgroundColor: uni('bg-static-content-highlight'),
  [tabletBreakpoint]: {
    borderRadius: uni('border-radius-sm'),
  },
}));

export const ReceiverDisclaimerIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box',
  width: '24px',
  height: '24px',
  '& svg': { display: 'block', width: '100%', height: '100%' },
});

export const ReceiverDisclaimerText = styled('p')`
  ${uniTypography('text-md', 'medium')}
  color: ${uni('fg-neutral-static-default-heading')};
  margin: 0;
  width: 100%;
  word-break: break-word;
`;
